import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { map, pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { Encrypt, getDateTime } from '@shared/utils';
import { DateTime } from 'luxon';
import { InviteActivationService } from './infra/invite-activation.service';
import {
  IInviteActivation,
  IInviteActivationCompleteForm,
  IInviteActivationCompletePayload,
  REQUIRES_CURRENT_PASSWORD_HINT,
  toInviteActivation,
} from './entities/invite-activation.interface';

export interface InviteActivationState {
  activation: IInviteActivation | null;
  requiresCurrentPassword: boolean;
}

const INITIAL_STATE: InviteActivationState = {
  activation: null,
  requiresCurrentPassword: false,
};

const REQ_STATE_PROPS = ['start', 'complete'] as const;

export const InviteActivationStore = signalStore(
  { providedIn: 'root' },
  withState<InviteActivationState>(INITIAL_STATE),
  withReqState(...REQ_STATE_PROPS),
  withMethods(
    (
      store,
      inviteActivationService = inject(InviteActivationService),
      errorStore = inject(ErrorStore),
      crypt = inject(Encrypt)
    ) => {
      const hasActivationExpired = (): boolean => {
        const expiresIn = store.activation()?.ExpiresIn;
        return !!expiresIn && getDateTime(expiresIn) < DateTime.now();
      };

      const buildCompletePayload = (
        form: IInviteActivationCompleteForm
      ): IInviteActivationCompletePayload => {
        const activation = store.activation();
        const email = (form.Email ?? activation?.Email ?? '').trim().toLowerCase();
        const payload: IInviteActivationCompletePayload = {
          ActivationToken: activation?.ActivationToken ?? '',
        };

        if (form.Email) {
          payload.Email = form.Email;
        }

        if (form.Password) {
          payload.Password = crypt.encryptMd5(email + form.Password);
        }

        if (form.CurrentPassword) {
          payload.CurrentPassword = crypt.encryptMd5(email + form.CurrentPassword);
        }

        return payload;
      };

      const start = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('start'))),
          switchMap((inviteToken) =>
            inviteActivationService.start({ InviteToken: inviteToken }).pipe(
              tapResponse({
                next: (response) =>
                  patchState(store, {
                    activation: toInviteActivation(response),
                    ...setPropLoaded('start'),
                  }),
                error: (error: HttpErrorResponse) => {
                  patchState(store, { activation: null, ...setPropError('start', error) });
                  errorStore.setHttpError(error);
                },
              })
            )
          )
        )
      );

      const complete = rxMethod<IInviteActivationCompleteForm>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('complete') })),
          map((form) => buildCompletePayload(form)),
          switchMap((payload) =>
            inviteActivationService
              .complete(payload)
              .pipe(
                tapResponse({
                  next: () => patchState(store, { ...setPropLoaded('complete') }),
                  error: (error: HttpErrorResponse) => {
                    const message = errorStore.getHttpErrorMessage(error);
                    patchState(store, {
                      requiresCurrentPassword:
                        store.requiresCurrentPassword() ||
                        (typeof message === 'string' &&
                          REQUIRES_CURRENT_PASSWORD_HINT.test(message)),
                      ...setPropError('complete', error),
                    });

                    if (hasActivationExpired()) {
                      patchState(store, { activation: null });
                    }

                    errorStore.setHttpError(error);
                  },
                })
              )
          )
        )
      );

      return {
        start,
        complete,
        reset(): void {
          patchState(store, INITIAL_STATE, setPropInit(...REQ_STATE_PROPS));
        },
      };
    }
  )
);
